import { useEffect } from 'react';
import { inputService } from '../services/InputService';
import { useDevtoolStore } from '../store/devtoolSlice';
import { useGameStore } from '../store/gameSlice';
import { dungeonService } from '../services/DungeonService';
import { mapService } from '../services/MapService';
import { openInterface } from '../services/InterfaceService';
import { PlayerEntity } from '../entities/PlayerEntity';
import { logger } from '../services/LoggerService';
import type { BuffDefinition } from '../types/buff.types';

const ONE_HIT_KO_BUFF: BuffDefinition = {
  id: 'dev-one-hit-ko',
  name: 'One-hit KO (dev)',
  internal: true,
  modifiers: [
    { stat: 'physicalAttack', amount: 1000 },
    { stat: 'magicAttack', amount: 1000 },
  ],
};

function resetDevCheats(): void {
  const player = dungeonService.getPlayer();
  if (!(player instanceof PlayerEntity)) {
    return;
  }
  let changed = false;
  if (player.godMode) {
    player.godMode = false;
    changed = true;
  }
  if (player.noclip) {
    player.noclip = false;
    changed = true;
  }
  const oneHitKo = player
    .getActiveBuffs()
    .find((b) => b.def.id === ONE_HIT_KO_BUFF.id);
  if (oneHitKo) {
    player.removeBuff(oneHitKo);
    player.recomputeStats();
    changed = true;
  }
  if (changed) {
    logger.info('Dev cheats reset (dev mode disabled)');
  }
}

export function useDevtoolHotkeys(): void {
  const devModeEnabled = useDevtoolStore((s) => s.devModeEnabled);

  useEffect(() => {
    if (!devModeEnabled) {
      return;
    }
    const unregister: Array<() => void> = [];

    unregister.push(
      inputService.registerHotkey('F1', () => {
        const { isPaused, setIsPaused } = useGameStore.getState();
        setIsPaused(!isPaused);
      }),
    );

    unregister.push(
      inputService.registerHotkey('F2', () => {
        useDevtoolStore.getState().toggleDevtoolPanelVisible();
      }),
    );

    unregister.push(
      inputService.registerHotkey('F3', () => {
        const player = dungeonService.getPlayer();
        if (player instanceof PlayerEntity) {
          player.godMode = !player.godMode;
          logger.info(`God mode : ${player.godMode ? 'ON' : 'OFF'}`);
        }
      }),
    );

    unregister.push(
      inputService.registerHotkey('F4', () => {
        const player = dungeonService.getPlayer();
        if (player instanceof PlayerEntity) {
          player.noclip = !player.noclip;
          logger.info(`No-clip : ${player.noclip ? 'ON' : 'OFF'}`);
        }
      }),
    );

    unregister.push(
      inputService.registerHotkey('F5', () => {
        useDevtoolStore.getState().toggleConsoleVisible();
      }),
    );

    unregister.push(
      inputService.registerHotkey('F6', () => {
        openInterface('dev-items');
      }),
    );

    unregister.push(
      inputService.registerHotkey('F7', () => {
        useDevtoolStore.getState().toggleGalleryOpen();
      }),
    );

    unregister.push(
      inputService.registerHotkey('Ctrl+KeyE', () => {
        dungeonService.emitGameEvent('nextFloor');
      }),
    );

    unregister.push(
      inputService.registerHotkey('Ctrl+KeyI', () => {
        const player = dungeonService.getPlayer();
        if (!(player instanceof PlayerEntity)) {
          return;
        }
        const existing = player
          .getActiveBuffs()
          .find((b) => b.def.id === ONE_HIT_KO_BUFF.id);
        if (existing) {
          player.removeBuff(existing);
        } else {
          player.addBuff({ def: ONE_HIT_KO_BUFF, remainingTicks: undefined });
        }
        player.recomputeStats();
        logger.info(`One-hit KO : ${existing ? 'OFF' : 'ON'}`);
      }),
    );

    unregister.push(
      inputService.registerHotkey('Ctrl+KeyU', () => {
        mapService.revealAll();
        logger.info('Map revealed');
      }),
    );

    unregister.push(
      inputService.registerHotkey('F12', () => {
        if (inputService.isKeyDown('F11')) {
          useDevtoolStore.getState().resetPopupLayouts();
        }
      }),
    );

    return () => {
      for (const u of unregister) {
        u();
      }
      resetDevCheats();
    };
  }, [devModeEnabled]);
}
